import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useCallback } from 'react';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';

import {
  LOGOUT_REDIRECT_URI,
  CLIENT_ID,
} from '#apps/client-web/features/auth/config/oidc';

export type Auth = ReturnType<typeof useAuth>;

export const useAuth = ({ logger }: LoggingContext = {}) => {
  const {
    isAuthenticated,
    isLoading,
    signinRedirect,
    signoutRedirect,
    user,
    error,
  } = useOidcAuth();

  const signInCallback = useCallback(() => {
    signinRedirect()
      .then(() => {
        logger?.info('Redirecting to the sign in page...');
      })
      .catch((err) => {
        logger?.error(err, 'Error signing in user:');
      });
  }, [signinRedirect]);

  const signOutCallback = useCallback(() => {
    signoutRedirect({
      extraQueryParams: {
        client_id: CLIENT_ID,
        logout_uri: LOGOUT_REDIRECT_URI,
      },
    })
      .then(() => {
        logger?.info('Redirecting to the sign out page...');
      })
      .catch((err) => {
        logger?.error(err, 'Error signing out user:');
      });
  }, [signoutRedirect]);

  return {
    isLoading,

    isError: !isNil(error),
    error,

    isSignedIn: isAuthenticated === true,
    isNotSignedIn: isAuthenticated !== true,

    onSignIn: signInCallback,
    onSignOut: signOutCallback,

    user: user,
  };
};
